import { useCallback, useEffect, useState } from 'react';
import { licenseApi } from '../api/licenseApi';
import ResellerCertificate from '../components/reseller/ResellerCertificate';
import ResellerLicenseSummary from '../components/reseller/ResellerLicenseSummary';
import { BackLink, LoadError, LoadSkeleton } from '../components/ui';
import { useAuthStore } from '../stores/authStore';
import { useEntitlementsStore } from '../stores/entitlementsStore';
import { useUiStore } from '../stores/uiStore';
import { downloadBlobFile } from '../lib/download';
import type { License } from '../types/domain';
import { paths } from './paths';

export default function Reseller() {
  const user = useAuthStore((state) => state.user);
  const ownsReseller = useEntitlementsStore((state) => state.reseller);
  const showToast = useUiStore((state) => state.showToast);
  const [license, setLicense] = useState<License | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const result = await licenseApi.getMyResellerLicense({
        ownsReseller,
        customerName: user.name,
        email: user.email,
      });
      if (!result) throw new Error('Your reseller license could not be found.');
      setLicense(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load your reseller license.');
    } finally {
      setLoading(false);
    }
  }, [ownsReseller, user]);

  useEffect(() => {
    void load();
  }, [load]);

  async function copyVerificationLink() {
    if (!license) return;
    await navigator.clipboard.writeText(license.verifyUrl);
    showToast('Verification link copied');
  }

  async function downloadCertificate() {
    if (downloading || !license) return;
    setDownloading(true);
    try {
      const { blob } = await licenseApi.downloadResellerCertificatePdf();
      downloadBlobFile(`Lyrixsa_AI_Reseller_License_${license.licenseNumber}.pdf`, blob);
      showToast('Reseller certificate PDF downloaded');
    } catch (cause) {
      showToast(
        cause instanceof Error ? cause.message : 'Could not download the reseller certificate'
      );
    } finally {
      setDownloading(false);
    }
  }

  if (loading)
    return <LoadSkeleton rows={3} rowHeight={110} label="Loading your reseller license…" />;
  if (error || !license)
    return (
      <LoadError
        message={error ?? 'Could not load your reseller license.'}
        onRetry={() => void load()}
      />
    );

  return (
    <div>
      <BackLink fallbackTo={paths.settings} fallbackLabel="Settings" />
      <div style={{ maxWidth: 800, margin: '0 auto 18px', textAlign: 'center' }}>
        <p
          style={{
            margin: '0 0 5px',
            color: '#F2A93C',
            fontSize: 11,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          Your reseller authorization
        </p>
        <h1 style={{ margin: 0, fontSize: 26 }}>Reseller License Certificate</h1>
      </div>
      <ResellerCertificate
        license={license}
        onDownload={() => void downloadCertificate()}
        downloading={downloading}
      />
      <ResellerLicenseSummary license={license} onCopy={() => void copyVerificationLink()} />
    </div>
  );
}
